import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Keyboard navigation helper for a linear list of results
 * (search dropdown, sidebar list, etc.).
 *
 * Returns:
 *   selectedIndex — currently highlighted item index (or -1)
 *   onKeyDown     — handler to bind to the input element
 *   listRef       — attach to the list container so we can auto-scroll
 *   itemRef(i)    — attach to each item to enable auto-scroll into view
 *   resetIndex    — reset highlight back to -1
 */
export function useListKeyboardNav({ itemsLength, onEnter, onEscape, enabled = true }) {
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const listRef = useRef(null);
    const itemRefs = useRef([]);

    // Reset highlight whenever the list shrinks past the current index
    // (typing narrows results, we don't want a phantom highlight).
    useEffect(() => {
        if (selectedIndex >= itemsLength) setSelectedIndex(-1);
    }, [itemsLength, selectedIndex]);

    // Auto-scroll the highlighted item into view when it changes.
    useEffect(() => {
        if (selectedIndex < 0) return;
        const el = itemRefs.current[selectedIndex];
        if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ block: "nearest" });
        }
    }, [selectedIndex]);

    const onKeyDown = useCallback((e) => {
        if (!enabled || !itemsLength) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex((i) => (i + 1) % itemsLength);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex((i) => (i <= 0 ? itemsLength - 1 : i - 1));
        } else if (e.key === "Enter") {
            if (selectedIndex >= 0 && selectedIndex < itemsLength) {
                e.preventDefault();
                onEnter?.(selectedIndex);
            }
        } else if (e.key === "Escape") {
            e.preventDefault();
            setSelectedIndex(-1);
            onEscape?.();
        }
    }, [itemsLength, selectedIndex, onEnter, onEscape, enabled]);

    const itemRef = useCallback((i) => (el) => {
        itemRefs.current[i] = el;
    }, []);

    const resetIndex = useCallback(() => setSelectedIndex(-1), []);

    return { selectedIndex, setSelectedIndex, onKeyDown, listRef, itemRef, resetIndex };
}
